import { useEffect, useId, useRef, useState } from 'react';
import { api } from '../api/client';
import { AudioProblem, clockTime, startRecording, toPhoneWav, type Recorder } from '../lib/audio';
import { Icon } from '../lib/icons';
import { TextAreaField } from './ui';

type Phase = { name: 'idle' } | { name: 'recording'; started: number } | { name: 'working'; what: string };

/**
 * An auto attendant greeting: words to read out, or a recording (uploaded, or recorded right
 * here). Recordings are converted for phones in the browser, uploaded to Stitch, and only reach
 * the phone system when the change is applied.
 */
export function GreetingEditor({
  tenant,
  label,
  help,
  text,
  onText,
  recording,
  recordingSeconds,
  onRecording,
  canRecord,
  loading,
  writer,
  error
}: {
  tenant: string;
  label: string;
  help?: string;
  text: string;
  onText: (v: string) => void;
  /** "sha256:..." when there is a recording, "" when there isn't, undefined when unknown. */
  recording: string | undefined;
  recordingSeconds?: number;
  onRecording: (ref: string, seconds?: number) => void;
  /** Recordings can be changed here. */
  canRecord: boolean;
  /** Still finding out whether there is a recording. */
  loading?: boolean;
  writer?: () => string;
  error?: string;
}) {
  const id = useId();
  const hasRecording = Boolean(recording);
  const [mode, setMode] = useState<'text' | 'recording'>(hasRecording ? 'recording' : 'text');
  const [phase, setPhase] = useState<Phase>({ name: 'idle' });
  const [problem, setProblem] = useState('');
  const [url, setUrl] = useState('');
  const [level, setLevel] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const recorder = useRef<Recorder | null>(null);
  const file = useRef<HTMLInputElement>(null);

  // Follow the value once it has loaded (an edit dialog opens before the recording is known).
  const [synced, setSynced] = useState(recording !== undefined);
  useEffect(() => {
    if (!synced && recording !== undefined) {
      setSynced(true);
      if (recording) setMode('recording');
    }
  }, [recording, synced]);

  // A playable copy of the current recording.
  useEffect(() => {
    let alive = true;
    let made = '';
    setUrl('');
    if (recording) {
      api
        .recordingUrl(tenant, recording)
        .then((u) => {
          made = u;
          if (alive) setUrl(u);
          else URL.revokeObjectURL(u);
        })
        .catch(() => undefined);
    }
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [tenant, recording]);

  // Stop the microphone if the dialog closes mid-recording.
  useEffect(() => () => recorder.current?.cancel(), []);

  useEffect(() => {
    if (phase.name !== 'recording') return;
    const t = window.setInterval(() => {
      setLevel(recorder.current?.level() ?? 0);
      setElapsed((Date.now() - phase.started) / 1000);
    }, 120);
    return () => window.clearInterval(t);
  }, [phase]);

  async function use(blob: Blob) {
    setProblem('');
    try {
      setPhase({ name: 'working', what: 'Converting for phones…' });
      const { wav } = await toPhoneWav(blob);
      setPhase({ name: 'working', what: 'Uploading…' });
      const up = await api.uploadRecording(tenant, wav);
      onRecording(up.ref, up.seconds);
    } catch (e) {
      setProblem(e instanceof AudioProblem || e instanceof Error ? e.message : 'That didn’t work. Try again.');
    } finally {
      setPhase({ name: 'idle' });
    }
  }

  async function record() {
    setProblem('');
    try {
      recorder.current = await startRecording();
      setElapsed(0);
      setPhase({ name: 'recording', started: Date.now() });
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'Recording didn’t start.');
    }
  }

  async function stop() {
    const r = recorder.current;
    recorder.current = null;
    if (!r) return;
    setPhase({ name: 'working', what: 'Finishing…' });
    await use(await r.stop());
  }

  const recordingTab = canRecord || hasRecording;
  const writeIt = writer && (
    <button type="button" className="link-btn write-it" onClick={() => onText(writer())}>
      <Icon name="spark" size={14} /> Write it for me
    </button>
  );
  if (!recordingTab) {
    return (
      <div className="speech-field">
        <TextAreaField label={label} hint={help} error={error} value={text} rows={3} onChange={(e) => onText(e.target.value)} />
        {writeIt}
      </div>
    );
  }
  // With the recording option, the heading above names the field; the text box uses it too.
  const textArea = (
    <div className="speech-field">
      <div className="field">
        <textarea
          className="input textarea"
          aria-labelledby={`${id}-label`}
          aria-describedby={help ? `${id}-hint` : undefined}
          aria-invalid={error ? true : undefined}
          value={text}
          rows={3}
          onChange={(e) => onText(e.target.value)}
        />
        {help && (
          <span className="hint" id={`${id}-hint`}>
            {help}
          </span>
        )}
        {error && <span className="error">{error}</span>}
      </div>
      {writeIt}
    </div>
  );

  return (
    <div className="greeting" role="group" aria-labelledby={`${id}-label`}>
      <div className="greeting-head">
        <span className="field-label" id={`${id}-label`}>
          {label}
        </span>
        <div className="segmented" role="radiogroup" aria-label="Greeting type">
          <label className={mode === 'text' ? 'on' : ''}>
            <input
              type="radio"
              name={`${id}-mode`}
              checked={mode === 'text'}
              disabled={!canRecord && hasRecording}
              onChange={() => {
                setMode('text');
                // Choosing text means the recording goes, so the words are read out.
                if (hasRecording) onRecording('');
              }}
            />
            Read out text
          </label>
          <label className={mode === 'recording' ? 'on' : ''}>
            <input type="radio" name={`${id}-mode`} checked={mode === 'recording'} onChange={() => setMode('recording')} />
            Play a recording
          </label>
        </div>
      </div>

      {mode === 'text' ? (
        textArea
      ) : (
        <div className="greeting-recording">
          {loading && recording === undefined ? (
            <span className="muted">Checking for a recording…</span>
          ) : hasRecording ? (
            <div className="recording-current">
              {url ? <audio controls src={url} preload="metadata" aria-label="Current recording" /> : <span className="muted">Loading the recording…</span>}
              {recordingSeconds !== undefined && <span className="muted">{clockTime(recordingSeconds)}</span>}
              {!canRecord && <span className="muted">Made in the phone system. Ask Stitch to change it.</span>}
            </div>
          ) : (
            <span className="muted">No recording yet. Upload one, or record it now.</span>
          )}

          {canRecord && (
            <div className="recording-actions">
              {phase.name === 'recording' ? (
                <>
                  <span className="rec-dot" aria-hidden="true" />
                  <span className="rec-time" aria-live="polite">
                    Recording {clockTime(elapsed)}
                  </span>
                  <span className="meter" aria-hidden="true">
                    <span style={{ width: `${Math.round(level * 100)}%` }} />
                  </span>
                  <button type="button" className="btn primary small" onClick={() => void stop()}>
                    <Icon name="stop" size={14} /> Stop
                  </button>
                  <button
                    type="button"
                    className="btn ghost small"
                    onClick={() => {
                      recorder.current?.cancel();
                      recorder.current = null;
                      setPhase({ name: 'idle' });
                    }}
                  >
                    Cancel
                  </button>
                </>
              ) : phase.name === 'working' ? (
                <span className="muted" aria-live="polite">
                  {phase.what}
                </span>
              ) : (
                <>
                  <button type="button" className="btn secondary small" onClick={() => file.current?.click()}>
                    <Icon name="upload" size={15} /> {hasRecording ? 'Replace with a file' : 'Upload a file'}
                  </button>
                  <button type="button" className="btn secondary small" onClick={() => void record()}>
                    <Icon name="mic" size={15} /> {hasRecording ? 'Record a new one' : 'Record now'}
                  </button>
                  <input
                    ref={file}
                    type="file"
                    accept="audio/*,.wav,.mp3,.m4a"
                    hidden
                    aria-label="Greeting audio file"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f) void use(f);
                    }}
                  />
                </>
              )}
            </div>
          )}
          {canRecord && <span className="hint">Any audio file works, up to 3 minutes. Stitch converts it for phones and trims silence.</span>}
          {problem && <span className="error">{problem}</span>}
        </div>
      )}
    </div>
  );
}
